import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Calendar, Clock, Users, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getStatus, showPrice, dateDisplay } from "@/lib/status";

export const DropSpotlight = ({ trip }) => {
  if (!trip) return null;
  const status = getStatus(trip.status);
  return (
    <section className="py-16 sm:py-20" data-testid="drop-spotlight-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-flame mb-8 flex items-center gap-2">
            <Flame className="w-4 h-4" /> Next Roamies Drop
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-12 border border-ink/15">
            <Link to={`/trips/${trip.slug}`} data-testid="drop-spotlight-image-link" className="lg:col-span-7 relative overflow-hidden group min-h-[380px] sm:min-h-[520px]">
              <img src={trip.image} alt={trip.title} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover saturate-[.9] transition-[transform,filter] duration-700 group-hover:scale-105 group-hover:saturate-100" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              {trip.drop_number && (
                <span className="absolute top-6 left-6 bg-cream text-ink text-xs font-bold tracking-[0.2em] uppercase px-4 py-2" data-testid="drop-number-badge">
                  Roamies {trip.drop_number}
                </span>
              )}
              <span className={`absolute top-6 right-6 text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 ${status.badge}`} data-testid="drop-status-badge">
                {status.label}
              </span>
            </Link>
            <div className="lg:col-span-5 bg-ink text-cream noise-bg p-8 sm:p-12 flex flex-col justify-between">
              <div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.9]" data-testid="drop-title">
                  {trip.title}
                </h2>
                <p className="mt-4 text-flame font-display text-xl uppercase tracking-wide" data-testid="drop-hook">
                  “{trip.hook || trip.tagline}”
                </p>
                <div className="mt-8 space-y-3 text-sm font-semibold text-cream/80" data-testid="drop-meta">
                  <p className="flex items-center gap-3"><MapPin className="w-4 h-4 text-flame shrink-0" />{trip.destination}</p>
                  <p className="flex items-center gap-3"><Calendar className="w-4 h-4 text-flame shrink-0" />{dateDisplay(trip)}</p>
                  <p className="flex items-center gap-3"><Clock className="w-4 h-4 text-flame shrink-0" />{trip.duration}</p>
                  <p className="flex items-center gap-3"><Users className="w-4 h-4 text-flame shrink-0" />Ages {trip.age_min || 18}–{trip.age_max || 45} · Small group · {trip.group_size}</p>
                </div>
                <p className="mt-6 text-cream/60 text-sm leading-relaxed">{trip.description?.slice(0, 160)}…</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-10">
                <Button asChild data-testid="drop-view-btn"
                  className="flex-1 bg-cream text-ink hover:bg-white rounded-none h-14 font-bold uppercase tracking-widest text-xs transition-transform hover:-translate-y-0.5">
                  <Link to={`/trips/${trip.slug}`}>View the Drop</Link>
                </Button>
                <Button asChild data-testid="drop-waitlist-btn"
                  className="flex-1 bg-flame hover:bg-flame/90 text-white rounded-none h-14 font-bold uppercase tracking-widest text-xs transition-transform hover:-translate-y-0.5">
                  <Link to={`/trips/${trip.slug}#waitlist`}>{status.cta} <ArrowRight className="w-4 h-4 ml-2" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
